/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { addDaysToDateKey, daysBetweenDateKeys, shiftByCalendarDays, toCalendarDateKey } from '@utils/calendar-days';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useDraggable } from '@dnd-kit/core';
import { DayCell } from './DayCell';
import './MonthGridStyles.scss';
const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** Builds the YYYY-MM-DD keys of the whole weeks covering the month (28, 35 or 42), Sunday first. */
function buildMonthDays(year, month) {
    const firstOfMonth = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const leadingDays = new Date(Date.UTC(year, month, 1)).getUTCDay();
    const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const cellCount = Math.ceil((leadingDays + daysInMonth) / 7) * 7;
    return Array.from({ length: cellCount }, (_, index) => addDaysToDateKey(firstOfMonth, index - leadingDays));
}
/** Groups events by their date key (YYYY-MM-DD) in the selected timezone. Multi-day events appear
 * on each day they span. End is exclusive (matching FullCalendar): an end exactly at midnight does
 * not occupy that day, while a sub-day end (time off arrives as 23:59:59 local) does. */
function buildEventMap(events, timezone) {
    const map = new Map();
    events.forEach((event) => {
        const firstKey = toCalendarDateKey(event.start, timezone);
        const endKey = toCalendarDateKey(new Date(event.end.getTime() - 1), timezone);
        const lastKey = endKey > firstKey ? endKey : firstKey;
        for (let key = firstKey; key <= lastKey; key = addDaysToDateKey(key, 1)) {
            const existing = map.get(key);
            if (existing) {
                existing.push(event);
            }
            else {
                map.set(key, [event]);
            }
        }
    });
    return map;
}
/**
 * Wraps a single event in a dnd-kit draggable.
 *
 * When dragging, this element becomes invisible (opacity 0) to act as a
 * placeholder. The actual drag visual is rendered by DragOverlay in MonthGrid,
 * which portals outside the cell DOM and is never clipped by overflow:hidden.
 */
function DraggableEvent({ event, dateKey, renderContent }) {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `${String(event.id)}:${dateKey}`,
        data: { event, dateKey },
        disabled: event.editable === false
    });
    return (React.createElement("div", Object.assign({ ref: setNodeRef, style: {
            opacity: isDragging ? 0 : 1,
            cursor: event.editable === false ? 'default' : 'grab'
        } }, listeners, attributes), renderContent(event)));
}
/**
 * Custom virtualized month grid.
 *
 * Replaces FullCalendar's dayGridMonth view when calendar optimization is
 * enabled. Only the visible events (up to dayMaxEvents per cell) are mounted;
 * overflow events are deferred to the MoreEventsPopover which mounts lazily.
 *
 * Drag-and-drop is handled by @dnd-kit/core with PointerSensor, providing
 * identical functionality to FullCalendar's month view drag behavior.
 */
export const MonthGrid = React.memo(function MonthGrid({ year, month, timezone = Intl.DateTimeFormat().resolvedOptions().timeZone, events, dayMaxEvents, eventContent, onEventClick, onEventChange, hidePreviousMonthEvents = false }) {
    var _a, _b;
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
    // The event currently being dragged — used to render the DragOverlay.
    const [activeDragEvent, setActiveDragEvent] = useState(null);
    const days = useMemo(() => buildMonthDays(year, month), [year, month]);
    const eventMap = useMemo(() => buildEventMap(events, timezone), [events, timezone]);
    const todayKey = toCalendarDateKey(new Date(), timezone);
    const currentMonthPrefix = `${year}-${String(month + 1).padStart(2, '0')}`;
    const handleDragStart = useCallback((dragStartEvent) => {
        var _a;
        const event = (_a = dragStartEvent.active.data.current) === null || _a === void 0 ? void 0 : _a.event;
        setActiveDragEvent(event !== null && event !== void 0 ? event : null);
    }, []);
    const handleDragEnd = useCallback((dragEvent) => {
        var _a, _b, _c;
        setActiveDragEvent(null);
        const { active, over } = dragEvent;
        if (!over)
            return;
        const draggedEvent = (_a = active.data.current) === null || _a === void 0 ? void 0 : _a.event;
        if (!draggedEvent)
            return;
        const sourceKey = (_c = (_b = active.data.current) === null || _b === void 0 ? void 0 : _b.dateKey) !== null && _c !== void 0 ? _c : toCalendarDateKey(draggedEvent.start, timezone);
        const dayOffset = daysBetweenDateKeys(sourceKey, over.id);
        if (Number.isNaN(dayOffset))
            return;
        onEventChange(draggedEvent, shiftByCalendarDays(draggedEvent.start, dayOffset, timezone), shiftByCalendarDays(draggedEvent.end, dayOffset, timezone));
    }, [onEventChange, timezone]);
    // Build a stable renderEvent function that wraps DraggableEvent + click handler
    const renderEvent = useCallback((event, dateKey) => (React.createElement(DraggableEvent, { event: event, dateKey: dateKey, renderContent: (e) => {
            var _a, _b, _c, _d;
            return (React.createElement("button", { type: "button", className: "qmb-month-grid__event-btn", onClick: () => onEventClick(e), style: {
                    backgroundColor: (_a = e.backgroundColor) !== null && _a !== void 0 ? _a : undefined,
                    borderColor: (_b = e.borderColor) !== null && _b !== void 0 ? _b : undefined,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    '--event-color': (_d = (_c = e.backgroundColor) !== null && _c !== void 0 ? _c : e.borderColor) !== null && _d !== void 0 ? _d : undefined
                } }, eventContent(e)));
        } })), [eventContent, onEventClick]);
    return (React.createElement(DndContext, { sensors: sensors, onDragStart: handleDragStart, onDragEnd: handleDragEnd },
        React.createElement("div", { className: "qmb-month-grid", "aria-label": `${dayjs(new Date(year, month)).format('MMMM YYYY')} calendar` },
            React.createElement("div", { className: "qmb-month-grid__header", "aria-hidden": "true" }, WEEK_DAYS.map((day) => (React.createElement("div", { key: day, className: "qmb-month-grid__header-cell" }, day)))),
            React.createElement("div", { className: "qmb-month-grid__body" }, days.map((dateKey) => {
                var _a;
                const dayEvents = hidePreviousMonthEvents && dateKey < `${currentMonthPrefix}-01` ? [] : (_a = eventMap.get(dateKey)) !== null && _a !== void 0 ? _a : [];
                return (React.createElement(DayCell, { key: dateKey, dateKey: dateKey, isCurrentMonth: dateKey.startsWith(currentMonthPrefix), isToday: dateKey === todayKey, events: dayEvents, dayMaxEvents: dayMaxEvents, renderEvent: renderEvent }));
            }))),
        React.createElement(DragOverlay, { dropAnimation: null }, activeDragEvent && (React.createElement("div", { className: "qmb-month-grid__drag-overlay" },
            React.createElement("button", { type: "button", className: "qmb-month-grid__event-btn", style: {
                    backgroundColor: (_a = activeDragEvent.backgroundColor) !== null && _a !== void 0 ? _a : undefined,
                    borderColor: (_b = activeDragEvent.borderColor) !== null && _b !== void 0 ? _b : undefined
                } }, eventContent(activeDragEvent)))))));
});
